<template>
  <div id="Qrcode" :title="'二维码'">
    <div class="qrcode_box">
      <canvas
        ref="qrCanvas"
        :width="qrSize"
        :height="qrSize"
        class="qrcode_canvas"
        :style="{ width: `${qrSize}px`, height: `${qrSize}px` }"
      />
      <!-- 重试按钮 -->
      <!--      <div v-if="showRetry" class="retry-container">-->
      <!--        <button-->
      <!--          class="retry-btn"-->
      <!--          @click="handleRetry"-->
      <!--          :disabled="isGenerating"-->
      <!--        >-->
      <!--          {{ isGenerating ? '生成中...' : '重试' }}-->
      <!--        </button>-->
      <!--      </div>-->
      <!-- 错误信息 -->
      <!--      <div v-if="errorMessage" class="error-message">-->
      <!--        {{ errorMessage }}-->
      <!--      </div>-->
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";

export default {
  name: "QRCodeGenerator",
  props: {
    qrUrl: {
      type: String,
      default: ""
    },
    qrSize: {
      type: Number,
      default: 150,
      validator: (value) => value > 0
    },
    qrText: {
      type: String,
      default: ""
    },
    qrLogo: {
      type: String,
      default: ""
    },
    qrLogoSize: {
      type: Number,
      default: 21,
      validator: (value) => value > 0
    },
    qrTextSize: {
      type: Number,
      default: 14,
      validator: (value) => value > 0
    },
    logoBorderRadius: {
      type: Number,
      default: 4
    },
    logoPadding: {
      type: Number,
      default: 4
    },
    textBackgroundColor: {
      type: String,
      default: "#fff"
    },
    textColor: {
      type: String,
      default: "#333"
    },
    errorCorrectionLevel: {
      type: String,
      default: "H",
      validator: (value) => ["L", "M", "Q", "H"].includes(value)
    },
    maxRetries: {
      type: Number,
      default: 3 // 最大重试次数
    },
    retryDelay: {
      type: Number,
      default: 1000 // 重试间隔（毫秒）
    },
    errorText: {
      type: String,
      default: "二维码生成失败"
    }
  },
  data() {
    return {
      isGenerating: false,
      retryCount: 0,
      errorMessage: "",
      showRetry: false
    };
  },
  methods: {
    /**
     * 生成带logo和文字的二维码（使用canvas绘制）
     */
    async generateQRCode() {
      if (!this.qrUrl || this.isGenerating) return;

      this.isGenerating = true;
      this.showRetry = false;
      this.errorMessage = "";

      // 确保DOM已更新
      await this.$nextTick();

      const canvas = this.$refs.qrCanvas;
      if (!canvas) {
        console.error("Canvas element not found");
        this.handleGenerationError("Canvas元素未找到");
        return;
      }

      const ctx = canvas.getContext("2d");

      try {
        // 清除画布
        ctx.clearRect(0, 0, this.qrSize, this.qrSize);

        // 1. 生成基础二维码并绘制
        const base64DataUrl = await QRCode.toDataURL(this.qrUrl, {
          width: this.qrSize,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#ffffff"
          },
          borderRadius: 10,
          errorCorrectionLevel: this.errorCorrectionLevel
        });

        const qrImage = new Image();
        qrImage.crossOrigin = "Anonymous";
        qrImage.src = base64DataUrl;

        await new Promise((resolve, reject) => {
          qrImage.onload = () => {
            // 绘制基础二维码
            ctx.drawImage(qrImage, 0, 0, this.qrSize, this.qrSize);
            resolve();
          };
          qrImage.onerror = () => {
            reject(new Error("基础二维码图片加载失败"));
          };
        });

        // 2. 绘制logo（如果提供）
        if (this.qrLogo) {
          await this.drawLogoOnCanvas(ctx);
        }

        // 3. 绘制文字（如果提供）
        if (this.qrText) {
          this.drawTextOnCanvas(ctx);
        }

        // 重置重试计数
        this.retryCount = 0;
      } catch (error) {
        console.error("生成二维码失败:", error);
        this.handleGenerationError(error.message || this.errorText);
      } finally {
        this.isGenerating = false;
      }
    },

    /**
     * 处理生成错误
     */
    handleGenerationError(errorMsg) {
      // 确保DOM已更新后再尝试访问canvas
      this.$nextTick(() => {
        const canvas = this.$refs.qrCanvas;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          // 清除画布并显示错误状态
          ctx.clearRect(0, 0, this.qrSize, this.qrSize);
          ctx.fillStyle = "#f8f9fa";
          ctx.fillRect(0, 0, this.qrSize, this.qrSize);
          ctx.fillStyle = "#6c757d";
          ctx.font = `${Math.max(10, this.qrSize/15)}px Arial`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(this.errorText || "二维码生成失败", this.qrSize/2, this.qrSize/2);
          // ctx.fillText("二维码生成失败", this.qrSize/2, this.qrSize/2 - 10);
          // ctx.fillText("点击重试", this.qrSize/2, this.qrSize/2 + 10);
        }
      });

      this.errorMessage = `错误: ${errorMsg}`;

      // 检查是否可以重试
      if (this.retryCount < this.maxRetries) {
        this.showRetry = true;
      } else {
        this.errorMessage += ` (已重试${this.maxRetries}次，达到最大重试次数)`;
      }
    },

    /**
     * 重试生成二维码
     */
    async handleRetry() {
      if (this.isGenerating) return;

      this.retryCount++;
      this.errorMessage = `正在重试 (${this.retryCount}/${this.maxRetries})...`;

      // 延迟一段时间再重试
      await new Promise(resolve => setTimeout(resolve, this.retryDelay));
      await this.generateQRCode();
    },

    /**
     * 在canvas上绘制logo
     */
    async drawLogoOnCanvas(ctx) {
      const logoImg = new Image();
      logoImg.crossOrigin = "Anonymous";
      logoImg.src = this.qrLogo;

      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => {
          reject(new Error("Logo图片加载失败"));
        };
      });

      const logoSize = this.qrLogoSize;
      const logoPadding = this.logoPadding;
      const totalLogoSize = logoSize + 2 * logoPadding;
      const centerX = (this.qrSize - totalLogoSize) / 2;
      const centerY = (this.qrSize - totalLogoSize) / 2;

      // 绘制圆角白色背景
      this.roundRect(
        ctx,
        centerX,
        centerY,
        totalLogoSize,
        totalLogoSize,
        this.logoBorderRadius
      );
      ctx.fillStyle = "#fff";
      ctx.fill();

      // 绘制logo
      ctx.drawImage(
        logoImg,
        centerX + logoPadding,
        centerY + logoPadding,
        logoSize,
        logoSize
      );
    },

    /**
     * 在canvas上绘制文字
     */
    drawTextOnCanvas(ctx) {
      const fontSize = this.qrTextSize;
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.textAlign = "center";

      // 计算文字背景区域
      const textMetrics = ctx.measureText(this.qrText);
      const textWidth = textMetrics.width;
      const padding = fontSize * 0.3;
      const bgWidth = textWidth + padding * 2;
      const bgHeight = fontSize + padding * 2;
      const x = this.qrSize / 2;
      const y = this.qrSize - bgHeight / 2;

      // 绘制文字背景
      ctx.fillStyle = this.textBackgroundColor;
      ctx.beginPath();
      ctx.moveTo(x - bgWidth / 2, y - bgHeight / 2);
      ctx.lineTo(x + bgWidth / 2, y - bgHeight / 2);
      ctx.lineTo(x + bgWidth / 2, y + bgHeight / 2);
      ctx.lineTo(x - bgWidth / 2, y + bgHeight / 2);
      ctx.closePath();
      ctx.fill();

      // 绘制文字
      ctx.fillStyle = this.textColor;
      ctx.textBaseline = "middle";
      ctx.fillText(this.qrText, x, y);
    },

    /**
     * 绘制圆角矩形辅助函数
     */
    roundRect(ctx, x, y, width, height, radius) {
      if (width < 2 * radius) radius = width / 2;
      if (height < 2 * radius) radius = height / 2;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + width, y, x + width, y + height, radius);
      ctx.arcTo(x + width, y + height, x, y + height, radius);
      ctx.arcTo(x, y + height, x, y, radius);
      ctx.arcTo(x, y, x + width, y, radius);
      ctx.closePath();
    }
  },
  mounted() {
    // 如果初始qrUrl为空，显示错误提示
    if (!this.qrUrl) {
      this.handleGenerationError("二维码内容为空");
    } else {
      // 确保在DOM完全渲染后再生成二维码
      this.$nextTick(() => {
        this.generateQRCode();
      });
    }
  },
  watch: {
    qrUrl: {
      handler(newVal) {
        if (!newVal) {
          this.handleGenerationError("二维码内容为空");
        } else {
          this.generateQRCode();
        }
      },
      immediate: true
    },
    qrSize() {
      this.generateQRCode();
    },
    qrText() {
      this.generateQRCode();
    },
    qrLogo() {
      this.generateQRCode();
    }
  }
};
</script>

<style lang="less" scoped>
#Qrcode {
  display: inline-block;
}

.qrcode_box {
  position: relative;
  display: inline-block;
}

.qrcode_canvas {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.retry-container {
  margin-top: 8px;
  text-align: center;
}

.retry-btn {
  padding: 6px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
}

.error-message {
  margin-top: 8px;
  padding: 8px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
}
</style>



